import { FC, PropsWithChildren, useEffect, useMemo } from "react";
import { makeStyles } from "tss-react/mui";
import { MiBuildHouseAlt } from "@miplatform/assets";
import {
  MiButton,
  MiDetailPageHeader,
  MiPlaceholderArea,
} from "@miplatform/components";
import { MIBUILD_PAGE_HEADER_HEIGHT } from "@miplatform/constants";
import { Box } from "@mui/material";
import { useProfile, useSession } from "../../hooks";

export const ConfigPageLayout: FC<ConfigViewProps> = ({
  title,
  children,
  bottomActions,
  titleRightAdornment,
  search,
  onHandleSearch,
  searchPlaceholder,
  titleActionComponent,
  loading,
  hideOnBack,
  customOnBack,
  renderTitle,
}) => {
  const { theme } = useStyles();

  const { session } = useSession();

  const { getProfile, profile } = useProfile();

  const defaultTitle = useMemo(() => {
    if (renderTitle) {
      return renderTitle();
    }
    if (!title && profile) {
      return `Welcome, ${profile.tenantName}`;
    }

    return title;
  }, [profile, title, renderTitle]);

  useEffect(() => {
    if (session.userId && !profile) {
      getProfile(session.userId);
    }
  }, [session.userId]);

  return (
    <>
      <MiDetailPageHeader
        title={defaultTitle}
        leftComponent={titleRightAdornment}
        search={search}
        onHandleSearch={onHandleSearch}
        searchPlaceholder={searchPlaceholder}
        actionComponent={titleActionComponent}
        loading={loading}
        style={{ backgroundColor: theme.palette.common.white }}
        customOnBack={customOnBack}
        hideOnBack={hideOnBack}
      />
      {children || (
        <MiPlaceholderArea
          text="Welcome to MiConfig"
          helperText="Get started by selecting a module or org settings to configure."
          iconComponent={<img src={MiBuildHouseAlt} alt="House Icon" />}
          containerStyle={{ backgroundColor: theme.palette.primary.light }}
        />
      )}
      <ConfigPageBottomActions bottomActions={bottomActions} />
    </>
  );
};

const ConfigPageBottomActions: FC<ConfigPageBottomActionsProps> = ({
  bottomActions,
}) => {
  const { classes } = useStyles();

  if (!bottomActions) {
    return null;
  }

  const {
    submit: { label: submitLabel, onClick: onSubmit, disabled: submitDisabled },
    cancel: {
      label: cancelLabel,
      onClick: onCancel,
      disabled: cancelDisabled,
    } = {},
  } = bottomActions;

  return (
    <Box className={classes.bottomContainer}>
      {cancelLabel && onCancel && (
        <MiButton
          variant="text"
          onClick={onCancel}
          disabled={cancelDisabled}
          className={classes.cancelButton}
        >
          {cancelLabel}
        </MiButton>
      )}
      <MiButton
        variant="contained"
        onClick={onSubmit}
        disabled={submitDisabled}
      >
        {submitLabel}
      </MiButton>
    </Box>
  );
};

interface ConfigViewProps
  extends PropsWithChildren,
    ConfigPageBottomActionsProps {
  title?: string;
  renderTitle?: () => React.ReactNode;
  titleRightAdornment?: React.ReactNode;
  search?: string;
  onHandleSearch?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  searchPlaceholder?: string;
  loading?: boolean;
  titleActionComponent?: React.ReactNode;
  hideOnBack?: boolean;
  customOnBack?: () => void;
}

interface ConfigPageBottomActionsProps {
  bottomActions?: {
    submit: {
      label: string;
      onClick: () => void;
      disabled?: boolean;
    };
    cancel?: {
      label: string;
      onClick: () => void;
      disabled?: boolean;
    };
  } | null;
}

const useStyles = makeStyles()((theme) => ({
  bottomContainer: {
    backgroundColor: theme.palette.common.white,
    position: "sticky",
    bottom: 0,
    zIndex: theme.zIndex.base,
    height: MIBUILD_PAGE_HEADER_HEIGHT,
    borderTop: `1px solid ${theme.palette.divider}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    padding: theme.spacing(2),
    gap: theme.spacing(2),
  },
  cancelButton: {
    color: theme.palette.common.black,
    textDecoration: "underline",
  },
}));
